/** /assistant/settings — which proactive messages Fusion AI sends, what it remembers about you, and standing instructions. */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import { ArrowLeft, Check, Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AK, call, errText, type AsMemory, type AsMemoryItem, type MemoryKind, type RhythmPref } from "../components/assistant/api";
import { useAssistantState } from "../components/assistant/FusionAi";
import { HandoffSessions } from "../components/assistant/Handoff";
import { Card, ErrorNote, PageHeader } from "../components/ui";

const PREFS: Array<{ key: RhythmPref; label: string; hint: string }> = [
  { key: "morning", label: "Morning brief", hint: "Weekdays around 8:00: what matters today." },
  { key: "meetings", label: "Before your meetings", hint: "A short brief about 15 minutes before a meeting, when there is something related." },
  { key: "nudges", label: "Worth a look", hint: "Weekdays around 11:00: decisions waiting, tasks moved again and again, renewals due, delegated work gone quiet." },
  { key: "wrap", label: "End of day", hint: "Weekdays around 17:30: cards for what to carry over." },
  { key: "weekly", label: "Weekly review", hint: "Friday afternoon: a nudge to close the week and plan the next." },
];

const KIND_LABEL: Record<MemoryKind, string> = { preference: "Preference", dont_remind: "Don't remind me", fact: "Fact" };
const rulesText = (r: unknown) => (typeof r === "string" ? r : r == null ? "" : Array.isArray(r) ? r.join("\n") : JSON.stringify(r, null, 2));

export function AssistantSettingsPage() {
  const memory = useQuery({ queryKey: AK.memory, queryFn: () => api<AsMemory>("/api/assistant/memory") });
  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/assistant" className="muted mb-3 inline-flex items-center gap-1 text-sm hover:text-brand-600"><ArrowLeft size={14} /> Fusion AI</Link>
      <PageHeader title="Fusion AI settings" subtitle="Choose what Fusion AI tells you on its own, and see what it remembers about you." />
      <ErrorNote error={memory.error} />
      <div className="space-y-4">
        <Card title="Proactive messages">
          {memory.isLoading ? <p className="muted text-sm">Loading…</p> : memory.data && <Prefs prefs={memory.data.prefs} />}
        </Card>
        <Card title="What Fusion AI remembers about you">
          {memory.isLoading ? <p className="muted text-sm">Loading…</p> : memory.data && <Memory items={memory.data.memory} />}
        </Card>
        <Card title="Standing instructions">
          <Rules />
        </Card>
        <HandoffSessions />
      </div>
    </div>
  );
}

function Switch({ on, disabled, label, onChange }: { on: boolean; disabled?: boolean; label: string; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={clsx("relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition disabled:opacity-60", on ? "bg-brand-600" : "bg-[var(--line-strong)]")}
    >
      <span className={clsx("inline-block h-5 w-5 rounded-full bg-white shadow transition", on ? "translate-x-[18px]" : "translate-x-0.5")} />
    </button>
  );
}

function Prefs({ prefs }: { prefs: Record<RhythmPref, boolean> }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<RhythmPref | null>(null);
  const [err, setErr] = useState("");
  const toggle = async (key: RhythmPref, value: boolean) => {
    setBusy(key); setErr("");
    qc.setQueryData<AsMemory>(AK.memory, (d) => (d ? { ...d, prefs: { ...d.prefs, [key]: value } } : d));
    const r = await call<{ prefs: Record<RhythmPref, boolean> }>("PUT", "/api/assistant/prefs", { [key]: value });
    if (r.ok && r.body?.prefs) qc.setQueryData<AsMemory>(AK.memory, (d) => (d ? { ...d, prefs: r.body!.prefs } : d));
    else { setErr(errText(r.body, r.status)); await qc.invalidateQueries({ queryKey: AK.memory }); }
    setBusy(null);
  };
  return (
    <>
      <ul className="divide-y divide-[var(--line)]">
        {PREFS.map((p) => (
          <li key={p.key} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{p.label}</div>
              <p className="muted text-xs">{p.hint}</p>
            </div>
            <Switch on={prefs[p.key] !== false} disabled={busy === p.key} label={p.label} onChange={(v) => void toggle(p.key, v)} />
          </li>
        ))}
      </ul>
      {err && <p role="alert" className="mt-2 text-sm text-red-600">{err}</p>}
    </>
  );
}

function Memory({ items }: { items: AsMemoryItem[] }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<MemoryKind>("preference");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<string>("");
  const [err, setErr] = useState("");
  const reload = () => qc.invalidateQueries({ queryKey: AK.memory });

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy("add"); setErr("");
    const r = await call("POST", "/api/assistant/memory", { kind, text: t });
    if (r.ok) setText(""); else setErr(errText(r.body, r.status));
    await reload();
    setBusy("");
  };
  const remove = async (id: string) => {
    setBusy(id); setErr("");
    const r = await call("DELETE", `/api/assistant/memory/${id}`);
    if (!r.ok) setErr(errText(r.body, r.status));
    await reload();
    setBusy("");
  };
  const clearAll = async () => {
    if (!window.confirm("Forget everything Fusion AI remembers about you? This cannot be undone.")) return;
    setBusy("clear"); setErr("");
    const r = await call("DELETE", "/api/assistant/memory");
    if (!r.ok) setErr(errText(r.body, r.status));
    await reload();
    setBusy("");
  };

  return (
    <div className="space-y-3">
      {items.length ? (
        <ul className="divide-y divide-[var(--line)]">
          {items.map((m) => (
            <li key={m.id} className="flex items-start gap-2 py-2 first:pt-0">
              <span className="chip mt-0.5 shrink-0">{KIND_LABEL[m.kind] ?? m.kind}</span>
              <div className="min-w-0 flex-1">
                <p className="break-words text-sm">{m.text}</p>
                <p className="subtle text-[11px]">
                  {new Date(m.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  {m.source ? ` · ${m.source === "settings" ? "added here" : m.source === "chat" ? "from a conversation" : m.source}` : ""}
                </p>
              </div>
              <button type="button" className="btn btn-ghost btn-icon !h-8 !min-h-0 !w-8 shrink-0 text-red-600" aria-label={`Forget: ${m.text}`} title="Forget this" disabled={!!busy} onClick={() => void remove(m.id)}>
                {busy === m.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted text-sm">Nothing yet. Tell Fusion AI "remember that…" or "don't remind me about…" in a conversation, or add something below.</p>
      )}
      <p className="muted text-xs">It also notices who you usually hand work to; that is worked out from your tasks each time and not stored.</p>

      <form className="flex flex-col gap-2 border-t border-[var(--line)] pt-3 sm:flex-row" onSubmit={(e) => { e.preventDefault(); void add(); }}>
        <select className="input !min-h-0 py-1.5 text-sm sm:w-44" aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value as MemoryKind)}>
          {(Object.keys(KIND_LABEL) as MemoryKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </select>
        <input className="input !min-h-0 min-w-0 flex-1 py-1.5 text-sm" aria-label="What to remember" placeholder={kind === "dont_remind" ? "e.g. the gym membership renewal" : kind === "fact" ? "e.g. My assistant is Maria" : "e.g. Keep briefs short"} maxLength={300} value={text} onChange={(e) => setText(e.target.value)} />
        <button type="submit" className="btn btn-primary btn-sm justify-center" disabled={!text.trim() || !!busy}>
          {busy === "add" ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add
        </button>
      </form>
      {err && <p role="alert" className="text-sm text-red-600">{err}</p>}
      {items.length > 0 && (
        <button type="button" className="btn btn-sm text-red-600" disabled={!!busy} onClick={() => void clearAll()}>
          {busy === "clear" ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Clear everything
        </button>
      )}
    </div>
  );
}

function Rules() {
  const qc = useQueryClient();
  const state = useAssistantState();
  const initial = rulesText(state.data?.rules);
  const [text, setText] = useState(initial);
  const [status, setStatus] = useState<"" | "saving" | "saved">("");
  const [err, setErr] = useState("");
  useEffect(() => { setText(initial); }, [initial]);
  const save = async () => {
    setStatus("saving"); setErr("");
    const r = await call("PUT", "/api/assistant/settings", { rules: text });
    if (!r.ok) { setStatus(""); setErr(errText(r.body, r.status)); return; }
    setStatus("saved");
    await qc.invalidateQueries({ queryKey: AK.state });
  };
  return (
    <div className="space-y-2">
      <p className="muted text-sm">Fusion AI follows these in every conversation (e.g. "Keep answers short", "My week starts on Monday").</p>
      <ErrorNote error={state.error} />
      <textarea className="input min-h-32 py-1.5 text-sm" aria-label="Standing instructions" maxLength={4000} value={text} disabled={state.isLoading} onChange={(e) => { setText(e.target.value); setStatus(""); }} />
      {err && <p role="alert" className="text-sm text-red-600">{err}</p>}
      <div className="flex items-center gap-2">
        <button type="button" className="btn btn-primary btn-sm" disabled={status === "saving" || text === initial} onClick={() => void save()}>{status === "saving" ? "Saving…" : "Save"}</button>
        {status === "saved" && <span className="text-xs text-emerald-700"><Check size={12} className="inline" /> Saved</span>}
      </div>
    </div>
  );
}
